
import Vue, { PropType } from 'vue';
import Api from '@/core/api';
import Icon from '@/common/icon/Icon.vue';
import { PlayerState } from '@/core/player-state';
import { get, isFunction } from 'lodash';
import { Unsubscriber } from '@/core/event-bus';
import canAutoPlay from 'can-autoplay';

interface UserInteractorConfig {
    destroyOnAutoPlay: boolean;
}

export default Vue.extend({
    name: 'UserInteractor',

    components: {
        Icon
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        config: {
            type: Object as PropType<UserInteractorConfig>,
            default: () => ({} as UserInteractorConfig)
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        }
    },

    data() {
        return {
            userHasInteracted: false as boolean,
            isPlaying: false as boolean,
            onStateChangeUnsubscriber: undefined as unknown as Unsubscriber,
            videoElapsedSecs: 0,
        };
    },

    computed: {
        destroyOnAutoPlay(): boolean {
            return get(this, 'config.destroyOnAutoPlay', true);
        }
    },

    async mounted() {
        const { unsubscriber, state } = this.api.onStateChange(
            (partial: Partial<PlayerState>) => {
                this.onNewState(partial);
            }
        );

        this.onNewState(state);

        this.onStateChangeUnsubscriber = unsubscriber;

        const canAutoPlayAudio = await canAutoPlay.video({ muted: false });
        if (canAutoPlayAudio.result) {
            this.destroy();
            return;
        }
    },

    beforeDestroy() {
        this.destroy();
    },

    methods: {
        click() {
            this.api.customAction({ clicked: true }, 'userInteractor');

            // if autoplay failed and the user interactor is still showing, the user will expect a start of the video unmuted on click (it's a big play button)
            this.api.unmute();
            this.api.play();

            this.destroy();
        },

        onNewState(newState:Partial<PlayerState>) {
            this.isPlaying = get(newState, 'isPlaying', this.isPlaying);
            this.videoElapsedSecs = get(newState, 'videoElapsedSecs', this.videoElapsedSecs);
            if (this.destroyOnAutoPlay && this.isPlaying && this.videoElapsedSecs) {
                this.destroy();
            }
        },

        destroy() {
            this.userHasInteracted = true;
            if (isFunction(this.onStateChangeUnsubscriber)) {
                this.onStateChangeUnsubscriber();
            }
        }
    },
});
